import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { api, type ListEnvelope } from './client';
import { useMallScopeStore } from '@/stores/mallScopeStore';
import type { ListParams } from '@/components/data/useServerTable';

/**
 * VERİ KATMANI.
 *
 * KAPSAM SORGU ANAHTARINA ZORLA EKLENİR.
 *
 * `useScopedQuery`, aktif AVM kimliğini her anahtarın başına koyar. Bunu
 * yapmadan, AVM değiştiğinde React Query önceki AVM'nin önbelleğini
 * geçerli sayar ve kullanıcı BAŞKA BİR AVM'NİN VERİSİNİ görür — üstelik
 * hiçbir hata çıkmadan.
 *
 * Bu, çoklu AVM sistemlerinde en sık ve en sessiz hatadır; bu yüzden
 * kapsam, çağıranın hatırlamasına bırakılmaz.
 */
function useScopeKey(): string {
  return useMallScopeStore((s) => s.mallId);
}

export function useScopedQuery<T>(
  key: readonly unknown[],
  fetcher: () => Promise<T>,
  options?: Omit<UseQueryOptions<T, Error, T, readonly unknown[]>, 'queryKey' | 'queryFn'>,
) {
  const scope = useScopeKey();

  /*
    KAPSAM BİLİNMEDEN SORGU ÇALIŞMAZ.

    Açılışta sıra şudur: React ağacı render edilir → App'in efekti profili
    okur → AVM kapsamı kurulur. Bu üç adım arasında kabuk (bildirim zili
    gibi) çoktan istek atmıştır ve o istek `X-Mall-Id` başlığı olmadan
    gider. Sunucu haklı olarak 400 döner ve React Query 4xx'i YENİDEN
    DENEMEZ — yani zil, kapsam geldikten sonra bile boş kalır.

    Tarayıcıda tam olarak bu yaşandı: `/workspace/notifications` 400,
    panel "Bildirim yok" diyordu ama API 20 kayıt döndürüyordu.

    Çözüm, her çağıranın `enabled` yazmayı hatırlamasına bırakılamaz;
    kapsamın kendisi burada bekletiliyor. `mallId` kurulunca sorgu
    kendiliğinden çalışır.
  */
  const hasScope = scope !== '';

  return useQuery<T, Error, T, readonly unknown[]>({
    queryKey: [scope, ...key],
    queryFn: fetcher,
    // Liste ekranlarında 30 sn boyunca yeniden istek atılmaz; sekmeye
    // geri dönüldüğünde tazelenir.
    staleTime: 30_000,
    ...options,
    enabled: hasScope && (options?.enabled ?? true),
  });
}

/** Tablo parametrelerini sorgu dizgesine çevirir. */
export function tableParams(params: ListParams): Record<string, string | number> {
  const query: Record<string, string | number> = {
    page: params.page,
    pageSize: params.pageSize,
  };

  if (params.q) query.q = params.q;
  if (params.sort) query.sort = `${params.sort.field}:${params.sort.direction}`;

  for (const [key, value] of Object.entries(params.filters ?? {})) {
    if (value) query[key] = String(value);
  }

  return query;
}

// =============================================================================
// PORTFÖY
// =============================================================================

export interface MallDto {
  id: string;
  name: string;
  city: string;
  code: string;
  gla: number;
  unitCount: number;
  openedYear: number | null;
}

export function useMalls() {
  return useQuery({
    queryKey: ['malls'],
    queryFn: () => api.get<{ items: MallDto[] }>('/malls', { skipMallScope: true }),
    // AVM listesi nadiren değişir ve her ekranda gerekir.
    staleTime: 5 * 60_000,
  });
}

export interface CategoryDto {
  id: string;
  code: string;
  name: string;
  color: string;
  typicalRateMin: number;
  typicalRateMax: number;
}

export function useCategories() {
  return useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get<{ items: CategoryDto[] }>('/categories', { skipMallScope: true }),
    staleTime: 5 * 60_000,
  });
}

export interface UnitDto {
  id: string;
  mallId: string;
  code: string;
  block: string;
  floor: string;
  gla: number;
  status: 'OCCUPIED' | 'VACANT' | 'UNDER_FIT_OUT' | 'RESERVED';
  vacantSince: string | null;
  categoryId: string | null;
  categoryName: string | null;
  categoryColor: string | null;
  currentLeaseId: string | null;
  currentBrandName: string | null;
  createdAt: string;
  updatedAt: string;
}

export function useUnits(params: ListParams, extra?: Record<string, string | undefined>) {
  return useScopedQuery(['units', params, extra], () =>
    api.get<ListEnvelope<UnitDto>>('/units', { query: { ...tableParams(params), ...extra } }),
  );
}

// =============================================================================
// KİRACI
// =============================================================================

export interface TenantDto {
  id: string;
  legalName: string;
  brandName: string;
  categoryId: string;
  categoryName: string;
  categoryColor: string;
  taxNumber: string;
  taxOffice: string | null;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  address: string | null;
  chainCode: string | null;
  leaseCount: number;
  mallCount: number;
  totalGla: number;
  createdAt: string;
  updatedAt: string;
}

export function useTenants(params: ListParams, extra?: Record<string, string | undefined>) {
  return useScopedQuery(['tenants', params, extra], () =>
    api.get<ListEnvelope<TenantDto>>('/tenants', { query: { ...tableParams(params), ...extra } }),
  );
}

// =============================================================================
// SÖZLEŞME
// =============================================================================

export interface LeaseDto {
  id: string;
  mallId: string;
  status: 'DRAFT' | 'ACTIVE' | 'EXPIRING' | 'EXPIRED' | 'TERMINATED';
  startDate: string;
  endDate: string;
  declarationFrequency: 'MONTHLY' | 'DAILY';
  includeOnlineSales: boolean;

  tenantId: string;
  brandName: string;
  legalName: string;
  taxNumber: string;
  contactEmail: string | null;

  unitId: string;
  unitCode: string;
  gla: number;
  floor: string;
  block: string;

  categoryId: string;
  categoryName: string;
  categoryColor: string;

  rentModel: 'FIXED' | 'PERCENT_OF_TURNOVER' | 'MAX_BASE_OR_TURNOVER' | 'TURNOVER_ABOVE_BREAKPOINT' | 'TIERED_TURNOVER' | null;
  currency: 'TRY' | 'USD' | 'EUR';
  comparisonCurrency: 'TRY' | 'USD' | 'EUR';
  minGuaranteeAmount: string | null;
  turnoverRatePercent: string | null;
  breakpointType: 'NATURAL' | 'ARTIFICIAL' | 'NONE';
  artificialBreakpoint: string | null;
  escalationBasis: string;
  escalationPercent: string | null;
  escalationMonth: number | null;
  camRatePerSqm: string;
  marketingFee: string;
  depositAmount: string | null;
  bankGuaranteeAmount: string | null;
  bankGuaranteeExpiry: string | null;
}

export function useLeases(params: ListParams, extra?: Record<string, string | undefined>) {
  return useScopedQuery(['leases', params, extra], () =>
    api.get<ListEnvelope<LeaseDto>>('/leases', { query: { ...tableParams(params), ...extra } }),
  );
}

/**
 * Ekran hesapları için sözleşmelerin TAMAMI.
 *
 * NEDEN AYRI BİR KANCA: Doluluk, teminat, kategori performansı gibi
 * ekranlar sayfalanmış liste değil, tüm kümeyi ister — bir KPI'ı ilk 25
 * satırdan hesaplamak yanlış rakam üretir. Sayfa boyutu üst sınıra
 * çekilir ve sonuç uzun süre önbellekte tutulur.
 */
export function useAllLeases(extra?: Record<string, string | undefined>) {
  return useScopedQuery(
    ['leases', 'all', extra],
    () =>
      api.get<ListEnvelope<LeaseDto>>('/leases', {
        query: { page: 1, pageSize: 200, ...extra },
      }),
    { staleTime: 60_000 },
  );
}

export function useAllUnits(extra?: Record<string, string | undefined>) {
  return useScopedQuery(
    ['units', 'all', extra],
    () => api.get<ListEnvelope<UnitDto>>('/units', { query: { page: 1, pageSize: 200, ...extra } }),
    { staleTime: 60_000 },
  );
}

// =============================================================================
// MUTASYONLAR
// =============================================================================

/**
 * Kayıt/güncelleme/silme sonrası önbellek tazeleme.
 *
 * NEDEN KABA TAZELEME (tüm anahtarı geçersiz kılma): İnce ayarlı önbellek
 * güncellemesi (satırı elle yerine koymak) daha hızlıdır ama sunucunun
 * hesapladığı türetilmiş alanları (durum, sayaçlar, toplamlar) kaçırır.
 * Liste ekranlarında bir ek istek, yanlış görünen bir satırdan ucuzdur.
 */
export function useInvalidate() {
  const queryClient = useQueryClient();

  return (...prefixes: string[]) => {
    for (const prefix of prefixes) {
      void queryClient.invalidateQueries({
        predicate: (query) => query.queryKey.includes(prefix),
      });
    }
  };
}

export function useApiMutation<TData, TVariables>(
  fn: (variables: TVariables) => Promise<TData>,
  invalidateKeys: string[],
  options?: Omit<UseMutationOptions<TData, Error, TVariables>, 'mutationFn'>,
) {
  const invalidate = useInvalidate();

  return useMutation<TData, Error, TVariables>({
    mutationFn: fn,
    ...options,
    onSuccess: (data, variables, context) => {
      invalidate(...invalidateKeys);
      void options?.onSuccess?.(data, variables, context, undefined as never);
    },
  });
}

// =============================================================================
// CİRO
// =============================================================================

export interface DeclarationDto {
  id: string;
  mallId: string;
  leaseId: string;
  period: string;
  status:
    | 'DRAFT'
    | 'SUBMITTED'
    | 'UNDER_REVIEW'
    | 'APPROVED'
    | 'REJECTED'
    | 'REVISION_REQUESTED'
    | 'LOCKED'
    | 'SUPERSEDED';
  source: 'PORTAL' | 'EXCEL' | 'MANUAL' | 'POS';
  grossAmount: string;
  returnsAmount: string;
  vatAmount: string;
  netAmount: string;
  amountBasis: string;
  transactionCount: number;
  revisionNo: number;
  anomalyScore: string | null;
  anomalyFlags: string[];
  hasEvidence: boolean;
  declaredAt: string;
  reviewedAt: string | null;
  reviewNote: string | null;
  tenantId: string;
  brandName: string;
  unitId: string;
  unitCode: string;
  gla: number;
  floor: string;
  categoryName: string;
  categoryColor: string | null;
  turnoverPerSqm: string;
  createdAt: string;
  updatedAt: string;
}

export function useDeclarations(params: ListParams, extra?: Record<string, string | undefined>) {
  return useScopedQuery(['declarations', params, extra], () =>
    api.get<ListEnvelope<DeclarationDto>>('/turnover-declarations', {
      query: { ...tableParams(params), ...extra },
    }),
  );
}

// =============================================================================
// FİNANS
// =============================================================================

export interface InvoiceDto {
  id: string;
  mallId: string;
  leaseId: string;
  invoiceNumber: string;
  series: string;
  status: 'DRAFT' | 'ISSUED' | 'PARTIALLY_PAID' | 'PAID' | 'CANCELLED' | 'CREDITED';
  issueDate: string;
  dueDate: string;
  periodStart: string;
  tenantId: string;
  brandName: string;
  legalName: string;
  taxNumber: string | null;
  unitCode: string;
  subtotal: string;
  vatAmount: string;
  total: string;
  paidAmount: string;
  balance: string;
  currency: 'TRY' | 'USD' | 'EUR';
  daysOverdue: number;
  agingBucket: 'CURRENT' | 'D1_30' | 'D31_60' | 'D61_90' | 'D90_PLUS';
  eInvoiceStatus: string;
  createdAt: string;
}

/** Sunucudan gelen filtrelenmiş toplamlar — tablo altında gösterilir. */
export interface InvoiceAggregates {
  total: string;
  paid: string;
  balance: string;
}

export function useInvoices(params: ListParams, extra?: Record<string, string | undefined>) {
  return useScopedQuery(['invoices', params, extra], () =>
    api.get<ListEnvelope<InvoiceDto> & { aggregates: InvoiceAggregates }>('/invoices', {
      query: { ...tableParams(params), ...extra },
    }),
  );
}

/**
 * Yaşlandırma ve tahsilat oranı için AÇIK faturaların tamamı.
 *
 * Sayfalanmış listeden yaşlandırma hesaplamak, kovaların sayfa değiştikçe
 * değişmesi demektir. Açık fatura sayısı bir AVM'de birkaç yüzü geçmez.
 */
export function useOpenInvoices() {
  return useScopedQuery(
    ['invoices', 'open'],
    () =>
      api.get<ListEnvelope<InvoiceDto> & { aggregates: InvoiceAggregates }>('/invoices', {
        query: { page: 1, pageSize: 200, aging: 'open' },
      }),
    { staleTime: 60_000 },
  );
}

export interface PaymentDto {
  id: string;
  mallId: string;
  leaseId: string;
  valueDate: string;
  method: 'TRANSFER' | 'DIRECT_DEBIT' | 'CHECK' | 'CREDIT_CARD' | 'CASH' | 'OFFSET';
  currency: 'TRY' | 'USD' | 'EUR';
  amount: string;
  amountTry: string;
  unallocatedAmount: string;
  reference: string | null;
  notes: string | null;
  tenantId: string;
  brandName: string;
  unitCode: string;
  allocations: Array<{ invoiceId: string; invoiceNumber: string; amount: string }>;
  allocatedTo: string[];
  createdAt: string;
}

export function usePayments(params: ListParams, extra?: Record<string, string | undefined>) {
  return useScopedQuery(['payments', params, extra], () =>
    api.get<ListEnvelope<PaymentDto> & { aggregates: { amountTry: string } }>('/payments', {
      query: { ...tableParams(params), ...extra },
    }),
  );
}

// =============================================================================
// OPERASYON
// =============================================================================

export interface TicketDto {
  id: string;
  mallId: string;
  ticketNumber: string;
  title: string;
  description: string | null;
  category: string;
  location: string | null;
  status: 'OPEN' | 'ASSIGNED' | 'IN_PROGRESS' | 'WAITING_TENANT' | 'RESOLVED' | 'CLOSED';
  priority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
  slaHours: number;
  slaDueAt: string;
  respondedAt: string | null;
  resolvedAt: string | null;
  resolution: string | null;
  assignedToId: string | null;
  leaseId: string | null;
  brandName: string | null;
  unitCode: string | null;
  isOverdue: boolean;
  createdAt: string;
  updatedAt: string;
}

export function useTickets(params: ListParams, extra?: Record<string, string | undefined>) {
  return useScopedQuery(['tickets', params, extra], () =>
    api.get<ListEnvelope<TicketDto>>('/tickets', { query: { ...tableParams(params), ...extra } }),
  );
}

export function useAllTickets() {
  return useScopedQuery(
    ['tickets', 'all'],
    () => api.get<ListEnvelope<TicketDto>>('/tickets', { query: { page: 1, pageSize: 200 } }),
    { staleTime: 60_000 },
  );
}

export interface AssetDto {
  id: string;
  mallId: string;
  code: string;
  name: string;
  category: string;
  location: string;
  manufacturer: string | null;
  model: string | null;
  serialNumber: string | null;
  installedYear: number;
  usefulLifeYears: number;
  remainingLifeYears: number;
  acquisitionCost: string | null;
  replacementCost: string | null;
  condition: 'GOOD' | 'FAIR' | 'POOR' | 'END_OF_LIFE';
  warrantyUntil: string | null;
  isUnderWarranty: boolean;
  maintenanceIntervalDays: number | null;
  lastMaintenanceAt: string | null;
  nextMaintenanceAt: string | null;
  isMaintenanceOverdue: boolean;
  isLegallyRequired: boolean;
  createdAt: string;
  updatedAt: string;
}

export function useAssets(params: ListParams, extra?: Record<string, string | undefined>) {
  return useScopedQuery(['assets', params, extra], () =>
    api.get<ListEnvelope<AssetDto>>('/assets', { query: { ...tableParams(params), ...extra } }),
  );
}

export interface MeterDto {
  id: string;
  mallId: string;
  meterNo: string;
  utilityType: 'ELECTRICITY' | 'WATER' | 'GAS';
  location: string;
  isMain: boolean;
  isCommonArea: boolean;
  leaseId: string | null;
  brandName: string | null;
  unitCode: string | null;
  lastPeriod: string | null;
  lastReading: string | null;
  lastConsumption: string | null;
  previousConsumption: string | null;
  consumptionChangePercent: string | null;
  readAt: string | null;
  createdAt: string;
}

export function useMeters(params: ListParams, extra?: Record<string, string | undefined>) {
  return useScopedQuery(['meters', params, extra], () =>
    api.get<ListEnvelope<MeterDto>>('/meters', { query: { ...tableParams(params), ...extra } }),
  );
}

export interface WorkOrderDto {
  id: string;
  orderNumber: string;
  title: string;
  status: string;
  priority: string;
  isPreventive: boolean;
  scheduledFor: string | null;
  completedAt: string | null;
  assignedToId: string | null;
  contractorName: string | null;
  assetId: string | null;
  totalCost: string;
  createdAt: string;
}

export function useWorkOrders(extra?: Record<string, string | undefined>) {
  return useScopedQuery(['workOrders', extra], () =>
    api.get<{ items: WorkOrderDto[]; total?: number }>('/work-orders', { query: extra }),
  );
}

// =============================================================================
// PAZARLAMA
// =============================================================================

export interface EventDto {
  id: string;
  mallId: string;
  code: string;
  name: string;
  eventType: 'SEASONAL' | 'CHILDREN' | 'CONCERT' | 'WORKSHOP' | 'CAMPAIGN' | 'LOCAL';
  venue: string;
  startDate: string;
  endDate: string;
  status: 'PLANNED' | 'RUNNING' | 'COMPLETED' | 'CANCELLED';
  budget: string;
  actualCost: string;
  currency: 'TRY' | 'USD' | 'EUR';
  budgetVariancePercent: string | null;
  footfallUplift: string | null;
  turnoverUplift: string | null;
  createdAt: string;
  updatedAt: string;
}

export function useEvents(params: ListParams, extra?: Record<string, string | undefined>) {
  return useScopedQuery(['events', params, extra], () =>
    api.get<ListEnvelope<EventDto>>('/events', { query: { ...tableParams(params), ...extra } }),
  );
}

export interface SpecialtySpaceDto {
  id: string;
  mallId: string;
  code: string;
  spaceType: 'KIOSK' | 'STAND' | 'ADVERTISING' | 'EVENT_AREA' | 'ATM' | 'VENDING';
  location: string;
  listMonthlyFee: string;
  isOccupied: boolean;
  currentTenantName: string | null;
  currentEndDate: string | null;
  currentMonthlyFee: string | null;
  discountPercent: string | null;
  nextTenantName: string | null;
  nextStartDate: string | null;
  bookingCount: number;
  ytdOccupancyPercent: string;
}

export function useSpecialtySpaces(params: ListParams, extra?: Record<string, string | undefined>) {
  return useScopedQuery(['specialtySpaces', params, extra], () =>
    api.get<ListEnvelope<SpecialtySpaceDto>>('/specialty-spaces', {
      query: { ...tableParams(params), ...extra },
    }),
  );
}

// =============================================================================
// UYUM & GÜVENLİK
// =============================================================================

export interface ComplianceDocumentDto {
  id: string;
  leaseId: string;
  mallId: string;
  tenantId: string;
  brandName: string;
  legalName: string;
  unitCode: string;
  docType: string;
  documentNo: string | null;
  issueDate: string | null;
  expiryDate: string | null;
  amount: string | null;
  isSubmitted: boolean;
  daysToExpiry: number | null;
  state: 'MISSING' | 'EXPIRED' | 'EXPIRING' | 'VALID';
  createdAt: string;
  updatedAt: string;
}

export function useComplianceDocuments(
  params: ListParams,
  extra?: Record<string, string | undefined>,
) {
  return useScopedQuery(['complianceDocuments', params, extra], () =>
    api.get<ListEnvelope<ComplianceDocumentDto>>('/compliance-documents', {
      query: { ...tableParams(params), ...extra },
    }),
  );
}

export interface SecurityIncidentDto {
  id: string;
  mallId: string;
  code: string;
  incidentType: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  location: string;
  occurredAt: string;
  reportedBy: string;
  summary: string;
  status: 'OPEN' | 'INVESTIGATING' | 'CLOSED';
  requiresOfficialReport: boolean;
  reportedToAuthority: boolean;
  reportDueAt: string | null;
  reportedAt: string | null;
  isReportOverdue: boolean;
  addendum: string | null;
  createdAt: string;
}

export function useSecurityIncidents(
  params: ListParams,
  extra?: Record<string, string | undefined>,
) {
  return useScopedQuery(['securityIncidents', params, extra], () =>
    api.get<ListEnvelope<SecurityIncidentDto>>('/security-incidents', {
      query: { ...tableParams(params), ...extra },
    }),
  );
}

// =============================================================================
// ANALİTİK
// =============================================================================

export interface DashboardDto {
  period: string;
  occupancy: {
    totalUnits: number;
    occupiedUnits: number;
    vacantUnits: number;
    totalGla: string;
    occupiedGla: string;
    ratePercent: string;
  };
  turnover: {
    current: string;
    previous: string;
    momPercent: string | null;
    yoyPercent: string | null;
    perSqm: string;
    /** Gönderilmiş ama henüz onaylanmamış beyanların net tutarı. */
    pending: string;
    trend: Array<{ period: string; turnover: string }>;
  };
  declarations: { expected: number; received: number; completionPercent: string };
  receivables: { open: string; overdue: string; overdueSharePercent: string };
  operations: { openTickets: number; overdueTickets: number; urgentTickets: number };
  leases: { active: number; expiringIn180Days: number };
}

export function useDashboard(period?: string) {
  return useScopedQuery(['dashboard', period], () =>
    api.get<DashboardDto>('/analytics/dashboard', { query: { period } }),
  );
}

export interface TurnoverTenantRow {
  tenantId: string;
  brandName: string;
  unitCode: string;
  categoryName: string;
  gla: number;
  turnover: string;
  transactionCount: number;
  turnoverPerSqm: string;
  averageBasket: string | null;
}

export interface TurnoverAnalyticsDto {
  period: string;
  total: string;
  transactionCount: number;
  trend: Array<{ period: string; turnover: string }>;
  categories: Array<{
    id: string;
    name: string;
    color: string;
    turnover: string;
    gla: string;
    turnoverPerSqm: string;
  }>;
  floors: Array<{ floor: string; turnover: string; gla: string; turnoverPerSqm: string }>;
  /** Dönemdeki TÜM kiracılar, cirodan büyükten küçüğe. */
  tenants: TurnoverTenantRow[];
  topTenants: TurnoverTenantRow[];
  bottomTenants: TurnoverTenantRow[];
}

export function useTurnoverAnalytics(period?: string, months?: number) {
  return useScopedQuery(['turnoverAnalytics', period, months], () =>
    api.get<TurnoverAnalyticsDto>('/analytics/turnover', { query: { period, months } }),
  );
}

export interface FootfallDto {
  daily: Array<{ date: string; visitors: number; parkingEntries: number }>;
  monthly: Array<{
    period: string;
    visitors: number;
    parkingEntries: number;
    dayCount: number;
    turnover: string;
    conversionPercent: string | null;
    spendPerVisitor: string | null;
  }>;
}

export function useFootfall(months?: number) {
  return useScopedQuery(['footfall', months], () =>
    api.get<FootfallDto>('/analytics/footfall', { query: { months } }),
  );
}

export interface PortfolioRowDto {
  id: string;
  name: string;
  code: string;
  city: string;
  unitCount: number;
  gla: string;
  occupancyPercent: string;
  turnover: string;
  turnoverPerSqm: string;
}

export function usePortfolio(period?: string) {
  return useScopedQuery(['portfolio', period], () =>
    api.get<{ period: string; items: PortfolioRowDto[] }>('/analytics/portfolio', {
      query: { period },
    }),
  );
}

// =============================================================================
// ÇALIŞMA ALANI — bildirim, arama, kullanıcı, denetim
// =============================================================================

export interface NotificationDto {
  id: string;
  eventType: string;
  title: string;
  tone: string;
  description: string;
  status: 'PENDING' | 'DISPATCHED' | 'FAILED' | 'DEAD';
  createdAt: string;
  dispatchedAt: string | null;
}

export function useNotifications() {
  return useScopedQuery(
    ['notifications'],
    () =>
      api.get<{ items: NotificationDto[]; total: number; unread: number }>(
        '/workspace/notifications',
        { query: { limit: 20 } },
      ),
    // Zil, sayfa açıkken de tazelenmeli: 60 saniyede bir yeterli.
    { staleTime: 30_000, refetchInterval: 60_000 },
  );
}

export interface SearchGroupDto {
  key: string;
  label: string;
  items: Array<{ id: string; title: string; subtitle: string; to: string }>;
}

export interface UserDto {
  id: string;
  email: string;
  fullName: string;
  userType: 'STAFF' | 'TENANT';
  isActive: boolean;
  mfaEnabled: boolean;
  lastLoginAt: string | null;
  lockedUntil: string | null;
  failedAttempts: number;
  tenantName: string | null;
  roles: string[];
  roleCodes: string[];
  malls: string[];
  createdAt: string;
}

export function useUsers(params: ListParams) {
  return useScopedQuery(['users', params], () =>
    api.get<ListEnvelope<UserDto>>('/workspace/users', { query: tableParams(params) }),
  );
}

export interface RoleDto {
  id: string;
  code: string;
  name: string;
  permissions: string[];
  permissionCount: number;
}

export function useRoles() {
  return useScopedQuery(
    ['roles'],
    () => api.get<{ items: RoleDto[]; total: number }>('/workspace/roles'),
    { staleTime: 5 * 60_000 },
  );
}

export interface AuditLogDto {
  id: string;
  entityType: string;
  entityId: string | null;
  action: string;
  actorId: string | null;
  /** Sunucuda çözülen ad — kimliğin kendisi okunabilir değil. */
  actorName: string;
  actorEmail: string | null;
  actorType: string;
  mallId: string | null;
  before: unknown;
  after: unknown;
  ipAddress: string | null;
  requestId: string | null;
  createdAt: string;
}

export function useAuditLog(params: ListParams) {
  return useScopedQuery(['auditLog', params], () =>
    api.get<ListEnvelope<AuditLogDto>>('/workspace/audit-log', { query: tableParams(params) }),
  );
}

export interface DemoDataStatusDto {
  loaded: boolean;
  summary: Record<string, number>;
}

export function useDemoDataStatus() {
  return useScopedQuery(['demoDataStatus'], () => api.get<DemoDataStatusDto>('/demo-data/status'));
}

// =============================================================================
// CİRO KİRASI HESABI
// =============================================================================

/** Motorun ürettiği hesap adımı — uyuşmazlıkta gösterilen döküm. */
export interface TraceStep {
  step: string;
  label?: string;
  value?: string;
  detail?: string;
  [key: string]: unknown;
}

export interface TurnoverRentRow {
  leaseId: string;
  tenantId: string;
  brandName: string;
  unitCode: string;
  floor: string;
  gla: number;
  categoryName: string;
  categoryColor: string | null;
  period: string;
  state: 'CALCULATED' | 'NO_DECLARATION' | 'FIXED' | 'NO_TERMS';
  rentModel: string | null;
  currency: string;
  minGuarantee: string | null;
  ratePercent: string | null;
  breakpoint: string | null;
  netTurnover: string | null;
  transactionCount?: number;
  declarationId?: string;
  declarationRevision?: number;
  turnoverRent: string | null;
  payableRent: string | null;
  difference: string | null;
  occupancyCostRatio: string | null;
  trace: TraceStep[];
}

export interface TurnoverRentResponse {
  period: string;
  items: TurnoverRentRow[];
  summary: {
    leaseCount: number;
    calculatedCount: number;
    missingDeclarationCount: number;
    fixedCount: number;
    aboveBreakpointCount: number;
    totalMinGuarantee: string;
    totalPayable: string;
    totalDifference: string;
  };
}

export function useTurnoverRent(period?: string) {
  return useScopedQuery(['turnoverRent', period], () =>
    api.get<TurnoverRentResponse>('/analytics/turnover-rent', { query: { period } }),
  );
}

// =============================================================================
// KİRACI PORTALI
// =============================================================================

/**
 * PORTAL SORGULARI AVM KAPSAMINA BAĞLI DEĞİLDİR.
 *
 * Kiracının hangi AVM'ye baktığı bir SEÇİM değil, sözleşmesinin sonucudur:
 * sunucu kapsamı token'dan türetir ve `X-Mall-Id` başlığını yalnızca ipucu
 * sayar. Bu yüzden portal sorguları `useScopedQuery`'nin AVM bekleme
 * kapısına takılmamalıdır — takılsaydı, tek AVM'si olan bir kiracı bile
 * kapsam kurulana kadar boş ekran görürdü.
 *
 * Anahtarın başına yine de sabit bir önek konur: personel oturumundan
 * kiracı oturumuna geçildiğinde önbellek çakışmasın diye.
 */
function usePortalQuery<T>(
  key: readonly unknown[],
  fetcher: () => Promise<T>,
  options?: Omit<UseQueryOptions<T, Error, T, readonly unknown[]>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<T, Error, T, readonly unknown[]>({
    queryKey: ['portal', ...key],
    queryFn: fetcher,
    staleTime: 30_000,
    ...options,
  });
}

export interface PortalLeaseDto {
  id: string;
  mallName: string;
  unitCode: string;
  floor: string;
  gla: number;
  rentModel: LeaseDto['rentModel'];
  currency: 'TRY' | 'USD' | 'EUR';
  minGuaranteeAmount: string | null;
  turnoverRatePercent: string | null;
  declarationFrequency: 'MONTHLY' | 'DAILY';
  includeOnlineSales: boolean;
  endDate: string;
}

export interface PortalSummaryDto {
  tenant: { id: string; brandName: string; legalName: string };
  leases: PortalLeaseDto[];
  pendingPeriods: Array<{ leaseId: string; unitCode: string; period: string }>;
  invoices: { openCount: number; overdueCount: number; outstanding: string };
  declarations: Array<{
    id: string;
    leaseId: string;
    period: string;
    status: DeclarationDto['status'];
    netAmount: string;
    grossAmount: string;
    transactionCount: number;
    revisionNo: number;
    declaredAt: string;
  }>;
}

export function usePortalSummary() {
  return usePortalQuery(['summary'], () => api.get<PortalSummaryDto>('/portal/summary'));
}

export interface PortalTurnoverDto {
  leases: Array<{
    id: string;
    unitCode: string;
    mallName: string;
    gla: number;
    startDate: string;
    turnoverRatePercent: string | null;
    minGuaranteeAmount: string | null;
    rentModel: LeaseDto['rentModel'];
    currency: 'TRY' | 'USD' | 'EUR';
  }>;
  items: Array<{
    id: string;
    leaseId: string;
    period: string;
    status: DeclarationDto['status'];
    source: DeclarationDto['source'];
    grossAmount: string;
    returnsAmount: string;
    vatAmount: string;
    netAmount: string;
    transactionCount: number;
    revisionNo: number;
    hasEvidence: boolean;
    declaredAt: string;
    reviewedAt: string | null;
    reviewNote: string | null;
  }>;
}

export function usePortalTurnover() {
  return usePortalQuery(['turnover'], () => api.get<PortalTurnoverDto>('/portal/turnover'));
}

export interface PortalInvoiceDto {
  id: string;
  invoiceNumber: string;
  status: InvoiceDto['status'];
  issueDate: string;
  dueDate: string;
  periodStart: string;
  unitCode: string;
  currency: 'TRY' | 'USD' | 'EUR';
  subtotal: string;
  vatAmount: string;
  total: string;
  paidAmount: string;
  remaining: string;
  isOverdue: boolean;
  daysOverdue: number;
  lines: Array<{
    id: string;
    chargeType: string;
    description: string;
    amount: string;
    vatRate: string;
    vatAmount: string;
  }>;
  payments: Array<{ amount: string; valueDate: string; method: string }>;
}

export function usePortalInvoices() {
  return usePortalQuery(['invoices'], () =>
    api.get<{ items: PortalInvoiceDto[]; totals: { outstanding: string; invoiced: string } }>(
      '/portal/invoices',
    ),
  );
}

export interface PortalTicketDto {
  id: string;
  ticketNumber: string;
  title: string;
  description: string | null;
  category: string;
  status: TicketDto['status'];
  priority: TicketDto['priority'];
  slaDueAt: string;
  respondedAt: string | null;
  resolvedAt: string | null;
  resolution: string | null;
  createdAt: string;
}

export function usePortalTickets() {
  return usePortalQuery(['tickets'], () =>
    api.get<{ items: PortalTicketDto[] }>('/portal/tickets'),
  );
}

export interface PortalAnnouncementDto {
  id: string;
  title: string;
  eventType: string;
  venue: string;
  mallName: string;
  startDate: string;
  endDate: string;
  status: string;
  isUpcoming: boolean;
}

export function usePortalAnnouncements() {
  return usePortalQuery(['announcements'], () =>
    api.get<{ items: PortalAnnouncementDto[] }>('/portal/announcements'),
  );
}

// =============================================================================
// TOPLU CİRO YÜKLEME
// =============================================================================

export interface ImportBaselineRow {
  leaseId: string;
  unitCode: string;
  floor: string;
  gla: number;
  brandName: string;
  rentModel: LeaseDto['rentModel'];
  turnoverRatePercent: string | null;
  /** Sözleşme bu dönemde yürürlükte mi? */
  inForce: boolean;
  /** Bu dönem için yaşayan bir beyan varsa durumu. */
  existingStatus: DeclarationDto['status'] | null;
  /** Son 12 dönemin ONAYLI ortalaması; örnek 3'ten azsa null. */
  averageNetAmount: string | null;
  averageSampleCount: number;
}

/**
 * Toplu yükleme doğrulama tabanı.
 *
 * Tek istek, üç soruyu birden cevaplar: ünite kodu hangi sözleşme, bu
 * dönemde beyan var mı, tutar geçmişe göre makul mü. Aynı bilgiyi
 * istemcide toplamak binlerce satırlık beyan geçmişini indirmek olurdu.
 */
export function useImportBaseline(period: string) {
  return useScopedQuery(
    ['importBaseline', period],
    () =>
      api.get<{ period: string; items: ImportBaselineRow[] }>(
        '/turnover-declarations/import-baseline',
        { query: { period } },
      ),
    { enabled: /^\d{4}-\d{2}$/.test(period), staleTime: 60_000 },
  );
}

export interface ImportResult {
  period: string;
  created: number;
  failed: number;
  results: Array<{
    rowNumber: number;
    leaseId: string;
    ok: boolean;
    declarationId?: string;
    message?: string;
  }>;
}

/**
 * DIŞA AKTARMA BİLDİRİMİ.
 *
 * CSV tarayıcıda üretildiği için sunucu indirmeyi göremez; bu çağrı onu
 * denetim kaydına yazar. Hata sessizce yutulur: denetim kaydı yazılamadı
 * diye kullanıcının indirmesini engellemek, çözdüğünden büyük bir sorun
 * üretir — ve kullanıcı zaten dosyayı almış olur.
 */
export function reportExport(input: {
  entityType: string;
  rowCount: number;
  format?: 'CSV' | 'XLSX' | 'PDF';
  filters?: Record<string, string>;
}): void {
  void api
    .post('/workspace/audit/export', { format: 'CSV', ...input })
    .catch(() => undefined);
}
